import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";

import { BACKEND_URL, formatApiError } from "@/lib/api";
import { BackHeader } from "@/components/back-header";
import { Button, ErrorBox, Field } from "@/components/ui";
import { PolicyFooter } from "@/components/policy-footer";

/** Public (no login) account deletion request — same backend form endpoint as /api/public/delete-account. */
export default function DeleteAccount() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return setError("Please enter a valid email.");
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/delete-account`, {
        method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ email: email.trim(), reason: reason.trim() }),
      });
      if (!res.ok) {
        let detail;
        try { detail = (await res.json()).detail; } catch { detail = null; }
        throw new Error(formatApiError(detail));
      }
      setSent(true);
    } catch (err) {
      setError(err.message === "Failed to fetch" ? "You appear to be offline. Check your connection and try again." : err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="delete-account-screen">
      <div className="mx-auto max-w-3xl px-6 py-8">
        <BackHeader title="Delete your account" fallback="/" />
        <h1 className="sr-only">Delete your LOVE NET account</h1>
        <section className="mb-8">
          <h2 className="mb-1 text-[24px]">Fastest: delete in the app</h2>
          <p className="text-[15px] leading-[23px]" data-testid="delete-account-in-app">
            Log in and open <b>Profile</b> → <b>Account Security</b> → <b>Delete account</b>, then confirm with your password.
            Your account is deactivated immediately and removed from discovery. Your photos and private WhatsApp contact are
            deleted, your profile is anonymised, and pending requests and connections end.
          </p>
          <Link to="/login" data-testid="delete-account-login-link" className="mt-2 inline-block text-sm font-bold text-primary underline">Log in to delete now</Link>
        </section>
        <section className="mb-8 rounded-[20px] border border-border bg-white p-6">
          <h2 className="mb-1 text-[24px]">Can't access your account? Request deletion</h2>
          {sent ? (
            <div className="mt-3 flex gap-3 rounded-xl bg-[#3A6C54]/10 p-4" data-testid="delete-account-success" role="status">
              <CheckCircle2 className="h-6 w-6 shrink-0 text-[#3A6C54]" />
              <p className="text-sm"><b>Request received.</b> We will confirm by email and delete your account within 30 days.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-3" data-testid="delete-account-form" noValidate>
              <Field testId="delete-account-email-input" label="Email used for your account" type="email" autoComplete="email" maxLength={200}
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              <Field testId="delete-account-reason-input" label="Reason (optional)" multiline maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
              {error ? <ErrorBox testId="delete-account-error">{error}</ErrorBox> : null}
              <Button testId="delete-account-submit-button" type="submit" title="Request account deletion" loading={busy} disabled={!email.trim()} className="mt-2 w-full sm:w-auto" />
            </form>
          )}
        </section>
        <section>
          <h2 className="mb-1 text-[24px]">What we keep</h2>
          <p className="text-[15px] leading-[23px]">
            Limited records (payment records, safety reports and audit logs) may be retained where the law requires or to prevent fraud
            and abuse, then deleted. Read the <Link to="/privacy" className="font-bold text-primary underline" data-testid="delete-account-privacy-link">Privacy Policy</Link>.
          </p>
        </section>
        <PolicyFooter />
      </div>
    </div>
  );
}
